import { useState, useCallback } from 'react';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';

export interface SongTopic {
  id: string;
  category: string;
  prompt: string;
  genreAffinity: string | null;
  timesUsed: number;
  lastUsedAt: string | null;
}

/**
 * Draws a topic from the DB-backed topic seed bank (category round-robin,
 * least-recently-used within category). Each draw marks the topic used, so
 * repeats naturally space out across days/sessions instead of resetting
 * every time the page reloads.
 *
 * Passing `genre` lets the draw prefer topics tagged as a fit for that
 * genre and deprioritize ones tagged for a different register (e.g. a
 * wedding-toast topic for a doom/sludge brief) — a soft nudge, not a hard
 * filter, since topics and genres aren't mutually exclusive.
 */
export function useSongTopics() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const drawTopic = useCallback(async (excludeIds: string[] = [], genre?: string): Promise<SongTopic | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithCsrf('/api/song-topics/draw', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ excludeIds, genre }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || `Draw topic failed (${res.status})`);
      }
      const { data } = await res.json() as { data: SongTopic };
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { drawTopic, loading, error };
}
